import { toast } from "sonner";
import { Badge } from "../ui/badge";
import { CheckCircle, Activity, Loader2 } from "lucide-react";
import type { LoadTestStatus } from "./types";

interface TestStatusBannerProps {
  status: LoadTestStatus | null;
  testId: string | null;
  isRunning: boolean;
  totalClients: number;
  runningTasks: number;
}

export const TestStatusBanner = ({
  status,
  testId,
  isRunning,
  totalClients,
  runningTasks,
}: TestStatusBannerProps) => {
  if (!status && !isRunning) return null;

  return (
    <div
      className={`flex justify-between items-center p-3 rounded-lg mb-4 border ${status?.status === "completed" ? "bg-muted" : "bg-muted"}`}
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center">
          {status?.status === "completed" ? (
            <CheckCircle className="h-5 w-5 text-primary" />
          ) : (
            <Activity className="h-5 w-5 text-primary animate-pulse" />
          )}
        </div>
        <div>
          <span className="font-semibold text-sm text-foreground">
            {status?.status === "completed"
              ? "Test Completed Successfully"
              : "Test Running..."}
          </span>
          {testId && (
            <div className="flex items-center gap-1 mt-0.5">
              <Badge
                variant="outline"
                className="font-mono text-[0.65rem] px-1.5 py-0 cursor-pointer hover:bg-muted"
                title="Click to copy test ID"
                onClick={() => {
                  navigator.clipboard.writeText(testId);
                  toast.success("Test ID copied to clipboard");
                }}
              >
                {testId.substring(0, 12)}...
              </Badge>
            </div>
          )}
        </div>
      </div>
      {isRunning && (
        <div className="flex items-center gap-3 text-sm">
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-background">
            <CheckCircle className="h-3.5 w-3.5 text-primary" />
            <span className="font-medium">{totalClients}</span>
            <span className="text-muted-foreground">done</span>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-background">
            <Loader2 className="h-3.5 w-3.5 text-primary animate-spin" />
            <span className="font-medium">{runningTasks}</span>
            <span className="text-muted-foreground">active</span>
          </div>
        </div>
      )}
    </div>
  );
};
